import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { isServer } from '../../utils/utils';
import { bffDelete } from '../bff';
import { BffQueryKey } from '../model/BffQueryKey';
import { useBasket } from './useBasket';
import type { MutationProps } from '../model/MutationProps';
import type { Basket } from './model';

export const useDeletePayment = (
    props?: MutationProps,
): UseMutationResult<Basket, unknown, string> => {
    const { data } = useBasket();
    const authContext = useAuthContext();
    const queryClient = useQueryClient();

    return useMutation<Basket, unknown, string>({
        mutationFn: async (paymentId: string) => {
            if (!isServer) await window.sfccSessionCreationPromise;
            const authData = await authContext.ready();

            try {
                return await bffDelete<Basket>(`/baskets/${data?.basketId}/payments/${paymentId}`, {
                    basketCustomerId: data?.customerInfo?.customerId,
                    accessToken: authData.access_token,
                    customerId: authData.customer_id,
                });
            } catch (error) {
                console.error('Ignoring error to delete payment:', error);
                return data!;
            }
        },
        onSuccess: async (basket): Promise<void> => {
            queryClient.setQueryData([BffQueryKey.BASKET], basket);

            if (!props?.skipInvalidateQueries) {
                await Promise.all([
                    queryClient.invalidateQueries([BffQueryKey.PAYMENT_METHODS], {
                        exact: false,
                    }),
                ]);
            }
        },
    });
};
